import React from 'react'
import { ChevronDown } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/utils/index'
import { useCountComboboxOpen } from '../hooks/use-count-combobox-open'

type CountComboboxInputProps = {
  id: string
  value: string
  onChange: (value: string) => void
  error: boolean
  placeholder: string
  options: number[]
}

/** Free-typed count input with a dropdown of whole-number options. */
export const CountComboboxInput: React.FC<CountComboboxInputProps> = ({
  id,
  value,
  onChange,
  error,
  placeholder,
  options,
}) => {
  const { open, setOpen, containerRef, inputRef } = useCountComboboxOpen()

  const normalizedValue = value.trim()

  return (
    <div ref={containerRef} className="relative">
      <Input
        ref={inputRef}
        id={id}
        value={value}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(event) => {
          onChange(event.target.value)
          setOpen(true)
        }}
        inputMode="numeric"
        placeholder={placeholder}
        error={error}
        className={cn(
          'h-9 rounded-md border-[var(--base-border,#E5E5E5)] bg-[var(--base-background,#FFF)] px-3 pr-10 text-sm text-[var(--base-foreground,#0A0A0A)] shadow-xs',
          'placeholder:text-[var(--base-muted-foreground,#737373)]',
          'focus-visible:border-[var(--base-border,#E5E5E5)] focus-visible:ring-[var(--ring,#144087)]/15'
        )}
      />
      <button
        type="button"
        aria-label="Toggle options"
        className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-[var(--base-muted-foreground,#737373)]"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          setOpen((prev) => !prev)
          inputRef.current?.focus()
        }}
      >
        <ChevronDown className={cn('h-5 w-5 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute top-[calc(100%+0.25rem)] left-0 z-50 w-full overflow-hidden rounded-[8px] border border-[var(--base-border,#E5E5E5)] bg-[var(--base-background,#FFF)] p-1 shadow-lg">
          <div className="max-h-72 overflow-y-auto overscroll-contain">
            {options.map((option) => {
              const optionValue = String(option)
              const selected = normalizedValue === optionValue

              return (
                <button
                  key={optionValue}
                  type="button"
                  className={cn(
                    'flex h-9 w-full items-center rounded-md px-3 text-left text-sm text-[var(--base-foreground,#0A0A0A)]',
                    'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
                    selected && 'bg-accent/60 text-accent-foreground'
                  )}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange(optionValue)
                    setOpen(false)
                    inputRef.current?.focus()
                  }}
                >
                  <span className="flex-1">{optionValue}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
